import React, { useState, useEffect, useCallback, useContext } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import AddIcon from "@material-ui/icons/Add";
import AssignmentIcon from "@material-ui/icons/Assignment";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import ImportantClientNoteModal from "../ImportantClientNoteModal";

const useStyles = makeStyles((theme) => ({
  header: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    marginBottom: 8
  },
  row: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    flexWrap: "wrap"
  },
  actions: {
    display: "flex",
    gap: 8,
    marginTop: 8
  }
}));

/**
 * Resumo compacto de Anotações Importantes para o ticket/contato.
 * Mostra contador de pendências abertas, destaca urgência/atraso e oferece
 * atalhos para criar uma nova anotação ou ver a lista filtrada pelo contato.
 */
const ImportantNotesWidget = ({ contactId, ticketId, customerName }) => {
  const classes = useStyles();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);

  const [summary, setSummary] = useState({
    openCount: 0,
    urgentCount: 0,
    lastOpenNote: null
  });
  const [modalOpen, setModalOpen] = useState(false);

  const fetchSummary = useCallback(async () => {
    if (!contactId) return;
    try {
      const { data } = await api.get("/important-client-notes/summary", {
        params: { contactId, ticketId }
      });
      setSummary(data);
    } catch (err) {
      toastError(err);
    }
  }, [contactId, ticketId]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    if (!socket) return undefined;
    const channel = `company-${user.companyId}-importantclientnote`;
    const onEvent = () => fetchSummary();
    socket.on(channel, onEvent);
    return () => socket.off(channel, onEvent);
  }, [socket, user.companyId, fetchSummary]);

  const isOverdue =
    summary.lastOpenNote?.dueAt &&
    new Date(summary.lastOpenNote.dueAt) < new Date(new Date().toDateString());
  const highlight = summary.urgentCount > 0 || isOverdue;

  const handleReload = () => fetchSummary();

  return (
    <>
      <div className={classes.header}>
        <AssignmentIcon
          fontSize="small"
          style={{ color: highlight ? "#d32f2f" : undefined }}
        />
        <Typography variant="subtitle1">
          {i18n.t("importantClientNotes.widget.title")}
        </Typography>
      </div>

      <div className={classes.row}>
        {summary.openCount > 0 ? (
          <Chip
            size="small"
            label={
              summary.openCount === 1
                ? i18n.t("importantClientNotes.widget.oneOpen")
                : `${summary.openCount} ${i18n.t(
                    "importantClientNotes.widget.manyOpen"
                  )}`
            }
            style={{
              backgroundColor: highlight ? "#d32f2f" : "#ed6c02",
              color: "#fff",
              fontWeight: 600
            }}
          />
        ) : (
          <Typography variant="body2" color="textSecondary">
            {i18n.t("importantClientNotes.widget.none")}
          </Typography>
        )}
        {summary.urgentCount > 0 && (
          <Chip
            size="small"
            label={`${summary.urgentCount} ${i18n.t(
              "importantClientNotes.widget.urgent"
            )}`}
            style={{ backgroundColor: "#d32f2f", color: "#fff" }}
          />
        )}
      </div>

      {summary.lastOpenNote && (
        <Typography
          variant="body2"
          color="textSecondary"
          style={{ marginTop: 6 }}
          noWrap
        >
          {summary.lastOpenNote.title}
        </Typography>
      )}

      <div className={classes.actions}>
        <Button
          size="small"
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setModalOpen(true)}
        >
          {i18n.t("importantClientNotes.widget.add")}
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="primary"
          onClick={() =>
            history.push(`/important-client-notes?contactId=${contactId}`)
          }
        >
          {i18n.t("importantClientNotes.widget.view")}
        </Button>
      </div>

      <ImportantClientNoteModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        reload={handleReload}
        defaults={{ contactId, ticketId, customerName }}
      />
    </>
  );
};

export default ImportantNotesWidget;
